<template>
  <div ref="commentForm" class="cf-CommentForm">
    <AppAvatar
      v-if="!isEdit"
      :avatar-url="myAvatar"
      class="cf-Avatar"
      data-testid="user-avatar"
      no-margins
    />
    <div class="cf-MessageAndActionsWrapper">
      <template v-if="active">
        <DescriptionField
          :ref="descriptionFieldReferenceKey"
          v-model="formModel.text"
          :allow-empty="false"
          :append-mention-to-body="appendMentionToBody"
          :blur-on-save="!replyTo"
          :disable="loading"
          :hidden-features="hiddenFeatures"
          :max-length="DESCRIPTION_FILED_LIMITS.COMMENT"
          :placeholder="$t('description.type_mention')"
          :placement="COMMENT_FIELD_PLACEMENTS"
          :readonly="loading"
          :short-name-text-format="isEditCommentOfCheckin"
          :table-actions-append-to
          :workspace-id="objective.workspaceId"
          data-testid="description-field"
          simple-view
          @cancel="cancel"
          @save="save"
        >
          <template v-if="isEditCommentOfCheckin" #toolbar-append>
            <div class="chf-TemplatesSelect">
              <DescriptionFieldTemplatesSelect
                :bottom-fixed-items="bottomFixedItems"
                :item-label="CHECKIN_TEMPLATE_ENTITY_KEYS.TITLE"
                :item-value="CHECKIN_TEMPLATE_ENTITY_KEYS.ID"
                :options="optionsList"
                @select-template="onTemplateSelect"
              />
            </div>
          </template>
        </DescriptionField>
      </template>
      <template v-else>
        <AppInput
          :placeholder="$t('description.type_mention')"
          class="cf-MessageAndActionsWrapper_Input"
          data-testid="comment-input"
          model-value=""
          size="lg"
          style-type="primary"
          @focus="onInputFocus"
        />
      </template>
    </div>

    <portal to="modal-windows">
      <CheckinTemplateModal
        :show="templateModalState.isShow"
        :template-content="templateModalState.templateContent"
        @template-saved="fetchTemplates"
        @on-close="closeTemplateModal"
      />
    </portal>
  </div>
</template>

<script>
import { isNull } from 'lodash'
import { computed, defineComponent, ref } from 'vue'

import CommentsApiHandler from '@/api/comments'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES, TRACKING_UNKNOWN } from '@/tracking/amplitude-helpers'
import { useCheckinTemplates } from '@/utils/check-ins/check-ins'
import { CHECKIN_TEMPLATE_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { commentFormInjectionKey } from '@/utils/injection-keys'
import { includesValue } from '@/utils/prop-validators'
import { DESCRIPTION_FILED_LIMITS } from '@/utils/rich-edit'
import { COMMENT_FIELD_PLACEMENTS } from '@/utils/tiptap-utils'
import { uid } from '@/utils/uid'
import { ELEMENT_VISIBILITY_CHECK_MODE, isVisibleInScrollableParent } from '@/utils/window'

import CheckinTemplateModal from '@/components/check-ins/CheckinTemplateModal'
import DescriptionField from '@/components/objectives/forms/DescriptionField'
import DescriptionFieldTemplatesSelect from '@/components/objectives/forms/DescriptionFieldTemplatesSelect'
import AppAvatar from '@/components/ui/AppAvatar/AppAvatar'
import AppInput from '@/components/ui/AppInput/AppInput'

const DEFAULT_FORM_MODEL = {
  text: ''
}

export default defineComponent({
  name: 'CommentForm',
  components: {
    CheckinTemplateModal,
    DescriptionFieldTemplatesSelect,
    AppInput,
    DescriptionField,
    AppAvatar
  },

  inject: {
    commentForm: {
      from: commentFormInjectionKey
    }
  },

  props: {
    modelValue: {
      type: Object,
      default: null
    },

    active: {
      type: Boolean,
      default: false
    },

    myAvatar: {
      type: String,
      default: null
    },

    replyTo: {
      type: Number,
      default: null
    },

    objective: {
      type: Object,
      default: null
    },

    appendMentionToBody: {
      type: Boolean,
      default: true
    },

    trackingSource: {
      type: String,
      default: TRACKING_UNKNOWN
    },

    trackingMode: {
      type: String,
      default: TRACKING_UNKNOWN
    },

    hiddenFeatures: {
      type: Array,
      default: () => []
    },

    autoScrollToFormMode: {
      type: String,
      default: ELEMENT_VISIBILITY_CHECK_MODE.SCROLLABLE_PARENT,
      validator: v => includesValue(v, ELEMENT_VISIBILITY_CHECK_MODE)
    },

    tableActionsAppendTo: {
      type: String,
      default: ''
    }
  },

  emits: { update: null, create: null, 'update:active': null },

  setup(props) {
    const formModel = ref({
      ...DEFAULT_FORM_MODEL
    })

    const isEdit = computed(() => !isNull(props.modelValue))

    const isEditCommentOfCheckin = computed(() => {
      return Boolean(isEdit.value && props.modelValue.checkinData)
    })

    const {
      bottomFixedItems,
      optionsList,
      onTemplateSelect,
      descriptionFieldReferenceKey,
      templateModalState,
      fetchTemplates,
      closeTemplateModal
    } = useCheckinTemplates({
      okrElement: () => props.objective,
      checkinCommentContent: () => formModel.value.text,
      fetchTemplatesOnStart: isEditCommentOfCheckin
    })

    return {
      isEdit,
      isEditCommentOfCheckin,
      formModel,
      bottomFixedItems,
      optionsList,
      onTemplateSelect,
      descriptionFieldReferenceKey,
      templateModalState,
      fetchTemplates,
      closeTemplateModal
    }
  },

  data() {
    return {
      uid: uid(),
      loading: false
    }
  },

  computed: {
    CHECKIN_TEMPLATE_ENTITY_KEYS: () => CHECKIN_TEMPLATE_ENTITY_KEYS,

    DESCRIPTION_FILED_LIMITS: () => DESCRIPTION_FILED_LIMITS,

    COMMENT_FIELD_PLACEMENTS: () => COMMENT_FIELD_PLACEMENTS
  },

  watch: {
    active: {
      handler(newValue) {
        if (newValue) {
          this.commentForm.opened = this.uid
          this.$nextTick(() => {
            this.focus()
          })
        } else {
          this.commentForm.opened = undefined
        }
      },

      immediate: true
    },

    'commentForm.opened': function handler(newValue) {
      if (newValue && this.uid !== newValue) {
        this.cancel()
      }
    }
  },

  mounted() {
    if (this.modelValue) {
      this.formModel.text = this.modelValue.text
    }
  },

  methods: {
    async save() {
      this.loading = true
      try {
        const { text } = this.formModel
        const commentsApi = new CommentsApiHandler()
        if (this.isEdit) {
          await commentsApi.updateComment({
            commentId: this.modelValue.id,
            text
          })
          this.$emit('update', { commentId: this.modelValue.id, text })

          tracker.logEvent('comment edited', {
            category: EVENT_CATEGORIES.COMMENTS,
            mode: 'main',
            source: this.trackingSource
          })
        } else {
          const commentsInfo = await commentsApi.createComment({
            objectiveId: this.objective.id,
            parentId: this.replyTo,
            text
          })
          this.$emit('create', commentsInfo)

          tracker.logEvent('comment left', {
            category: EVENT_CATEGORIES.COMMENTS,
            mode: this.trackingMode,
            source: this.trackingSource
          })
          this.formModel = { ...DEFAULT_FORM_MODEL }
        }
        this.$emit('update:active', false)
      } catch (error) {
        handleError({ error })
        await this.$nextTick()
        this.focus()
      } finally {
        this.loading = false
      }
    },

    cancel() {
      this.$emit('update:active', false)
      this.formModel = { ...DEFAULT_FORM_MODEL }
    },

    /** @public */
    focus() {
      // wait for DescriptionField rendering and then scroll
      setTimeout(() => {
        if (
          this.$refs.commentForm &&
          !isVisibleInScrollableParent({
            element: this.$refs.commentForm,
            mode: this.autoScrollToFormMode
          })
        ) {
          this.$el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
      }, 100)
      this.$refs[this.descriptionFieldReferenceKey]?.focus()
    },

    /* Public API */
    // eslint-disable-next-line vue/no-unused-properties
    replyOnComment(payload) {
      // wait for DescriptionField rendering and then scroll
      this.$refs[this.descriptionFieldReferenceKey]?.focus()
      this.$refs[this.descriptionFieldReferenceKey]?.replyOnComment(payload)
    },

    onInputFocus() {
      this.$emit('update:active', true)
    }
  }
})
</script>

<style lang="scss" scoped>
.cf-Avatar {
  margin-top: 8px;
}

.cf-CommentForm {
  display: flex;
  padding: var(--comment-form-padding, 8px 0);
  background-color: $white;
}

.cf-MessageAndActionsWrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.cf-MessageAndActionsWrapper_Input {
  &:deep(.o-input-wrapper .o-input-field) {
    font-weight: fw('regular');
    font-size: $fs-14;
    line-height: 20px;
    color: $dark-3;
    font-family: $system-ui;
  }
  &:deep(.o-input-wrapper) {
    border-color: $grey-2-next;
  }
}

.chf-TemplatesSelect {
  margin-left: auto;
}
</style>

<style lang="scss">
.cf-CommentForm {
  display: flex;
  gap: 8px;
  .o-textarea-field {
    background-color: $white;
    padding: 4px 10px;
    border: 1px solid $grey-10;

    &:focus {
      border-color: $primary-color;
    }

    &::placeholder {
      color: $blue-6;
      opacity: 0.5;
    }
  }
}
</style>
